<template>
  <!-- Full-screen on mobile; normal inline flow on sm+ -->
  <div
    class="fixed inset-0 z-50 flex flex-col bg-white p-4 overflow-y-auto sm:static sm:z-auto sm:p-0 sm:overflow-visible sm:h-full w-full"
  >
    <div v-if="isLoading" class="flex flex-col flex-auto justify-center text-center">
      <LayupSpinner />
      <p class="text-black">Loading...</p>
    </div>
    <div v-else class="flex flex-col text-left divide-y h-full w-full">
      <div class="flex cursor-pointer mb-3" @click="back">
        <img src="@/assets/icons/backArrow.svg" />
        <div class="self-center text-center text-lg font-semibold grow text-black">Checkout</div>
      </div>
      <ErrorDisplay v-if="errorMessage" :errorMessage="errorMessage" showBackButton />
      <div v-else-if="shouldShowPeach" class="grow py-1 mb-2">
        <div id="payment-form" :class="{ 'h-[500px]': checkoutFormLoaded }"></div>
      </div>
      <div class="flex flex-row py-2 mt-auto">
        <div class="flex flex-1 w-auto">
          <p class="text-extrabold text-base-black text-sm mr-1">Amount Due</p>
          <div class="mt-0.5">
            <ToolTip
              :text="'The amount due contributes towards or secures your product for the duration of the lay-by agreement.'"
            />
          </div>
        </div>
        <div class="font-bold text-base-black">
          {{ amountDisplay }}
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { createPayment } from '@/services/payments';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  waitForCheckoutSDK,
  waitForElement,
  type PeachCheckoutEvent,
  type PeachCompletedCheckoutEvent,
  type PeachErrorCheckoutEvent,
} from '../../../utils/peach.utils';
import ErrorDisplay from '../../common/ErrorDisplay.vue';

const widgetStore = useWidgetStore();

const router = useRouter();

const isLoading = ref(false);
const errorMessage = ref('');
const redirectParams = ref({ id: '' });
const checkoutFormLoaded = ref(false);
const paymentMethod = router.currentRoute.value.query.paymentMethod as string;
const amountDisplay = ref(displayCurrency(widgetStore.amountDue));

const shouldShowPeach = computed(() => {
  return (
    (paymentMethod === 'CARD' && widgetStore.order.paymentMethods.creditOrDebitCard) ||
    (paymentMethod === 'EFT' && widgetStore.order.paymentMethods.eft)
  );
});

const init = async () => {
  isLoading.value = true;
  if (!shouldShowPeach.value) {
    return router.replace({ name: 'SelectPaymentMethod' });
  }
  await makePayment();
};

const back = () => {
  const prev = window.history.state?.back as string | undefined;
  if (prev && prev.includes('paymentMethod')) {
    router.replace({ name: 'SelectPaymentMethod' });
  } else {
    router.back();
  }
};

const loadCheckoutForm = async (checkoutId: string, entityId: string) => {
  const sdkLoaded = await waitForCheckoutSDK();
  // Remove the loading indicator so that the peach widget can attach to the right element. The peach widget shows its own loading indicator.
  isLoading.value = false;
  if (!sdkLoaded) {
    createException({
      message: 'Checkout SDK failed to load after multiple attempts.',
      checkoutId,
      userAgent: navigator.userAgent,
    });
    errorMessage.value =
      'Our payment provider failed to load. Please check your internet connection and try again. If the problem persists, please try a different browser or contact support.';
    return;
  }

  try {
    const checkout = (window as any).Checkout.initiate({
      checkoutId,
      key: entityId, // Replace with your public entity key
      events: {
        /**
         * Event handlers fired when Checkout has completed successfully.
         * @param event Details about the transaction.
         * @returns Void
         */
        onCompleted: (event: PeachCompletedCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'success',
            value: event.amount,
            result: event.result.code,
            resultMessage: event.result.description,
          });
          router.replace({
            name: 'paymentStatus',
            params: redirectParams.value,
          });
        },
        /**
         * Event fired when Checkout has cancelled by the user.
         * @param event Details about the Checkout.
         * @returns Void
         */
        onCancelled: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'cancelled',
            result: event.result.code,
            resultMessage: event.result.description,
          });
          router.replace({
            name: 'paymentStatus',
            params: redirectParams.value,
          });
        },
        /**
         * Event fired when Checkout has timed out.
         * @param event Details about the Checkout.
         * @returns Void
         *
         * Customers have 30 minutes to complete a payment.
         */
        onExpired: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'expired',
            result: event.result.code,
            resultMessage: event.result.description,
          });
          router.replace({
            name: 'paymentStatus',
            params: redirectParams.value,
          });
        },
        /**
         * Event fired when a transaction has failed.
         * @param event Details about the failed transaction.
         * @returns Void
         */
        onError: (event: PeachErrorCheckoutEvent) => {
          const error = new Error('Peach widget error.');
          createWarning(error, {
            extra: {
              checkoutId,
              errorCode: event.result.code,
              errorDescription: event.result.description,
              userAgent: navigator.userAgent,
            },
          });
          addMetric('peach_widget', {
            status: 'error',
            result: event.result.code,
            resultMessage: event.result.description,
          });
          errorMessage.value =
            'Something went wrong while processing the payment. Please try again or contact support.';
        },
      },
      options: {
        theme: {
          brand: {
            primary: '#1295a5',
          },
        },
      },
    });

    const paymentFormElement = await waitForElement('#payment-form');
    checkout.render(paymentFormElement);
    checkoutFormLoaded.value = true;
  } catch (error) {
    addMetric('peach_widget', { status: 'peach_sdk_load_failure' });
    createException({
      message: 'Failed to initialize checkout form.',
      checkoutId,
      error,
      userAgent: navigator.userAgent,
    });
    errorMessage.value = 'Unable to load the payment form. Please try again or contact support.';
  }
};

const makePayment = async () => {
  try {
    const paymentPlanId = widgetStore.id;
    const notificationUrl = BASE_URL + '/shopper/payment';
    const response = await createPayment(
      paymentPlanId,
      widgetStore.amountDue / 100,
      notificationUrl,
      paymentMethod,
      'paymentPlan',
    );
    const paymentId = response._id;
    const { checkoutId, entityId } = response;
    redirectParams.value = { id: paymentId };
    await loadCheckoutForm(checkoutId, entityId!);
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Payment could not be created. Please contact support.';
      }
    }
  } finally {
    isLoading.value = false;
  }
};

init();
</script>
